// Family merge modal — pick a target family, preview how many variants will
// move, then confirm the destructive merge. Merge is move-only: every source
// variant moves to the target verbatim, similar variants are never collapsed.
// Entry points: the family workspace header's "Merge into…" button and the
// families list row action.

import {
  Box,
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import {
  MasterProductFamilyOption,
  useMergeFamilyApi,
  useMergeFamilyPreviewApi,
  useSearchMasterProductFamiliesApi,
} from 'apis/master-product-families';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { buildMergeSummary } from './merge-summary';

export interface MergeFamilyModalSourceFamily {
  id: number;
  universalName: string;
}

export interface MergeFamilyModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceFamily: MergeFamilyModalSourceFamily;
}

export const MergeFamilyModal: React.FC<MergeFamilyModalProps> = ({ isOpen, onClose, sourceFamily }) => {
  const history = useHistory();
  const toast = useCustomToast();

  const [target, setTarget] = useState<MasterProductFamilyOption | null>(null);

  // Reset on close so a fresh open never shows the previous pick/preview.
  useEffect(() => {
    if (!isOpen) setTarget(null);
  }, [isOpen]);

  const { loadOptions: rawLoadOptions } = useSearchMasterProductFamiliesApi();
  const loadOptions = useCallback(
    (inputValue: string, callback: (options: MasterProductFamilyOption[]) => void) => {
      rawLoadOptions(inputValue, (options) => callback(options.filter((o) => o.value !== sourceFamily.id)));
    },
    [rawLoadOptions, sourceFamily.id]
  );

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useMergeFamilyPreviewApi(sourceFamily.id, target?.value);

  const { mutate: mergeFamily, isLoading: isMerging } = useMergeFamilyApi({
    onSuccess: (result) => {
      toast({
        status: 'success',
        description: `Moved ${result.movedVariants} variant${result.movedVariants === 1 ? '' : 's'} to the target family.`,
      });
      onClose();
      // One-time highlight handoff: the target workspace consumes this state on
      // mount and immediately `history.replace`s it away (see
      // family-workspace-screen.tsx), so a later refresh/back never re-highlights.
      history.push(`/app/inquiry-processor/master-products/${result.targetFamilyId}`, {
        mergedVariantIds: result.movedVariantIds ?? [],
        mergeSourceName: sourceFamily.universalName,
      });
    },
  });

  const handleConfirm = () => {
    if (!target) return;
    mergeFamily({ sourceId: sourceFamily.id, targetFamilyId: target.value });
  };

  const summary = target && preview ? buildMergeSummary(preview, target.label) : null;
  const canConfirm = !!target && !!preview && !isPreviewLoading && !isPreviewError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: 'lg' }}
      scrollBehavior='inside'
      closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Merge Family</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Text>
              Merge <strong>{sourceFamily.universalName}</strong> into another family. Its variants move to the target
              family and this family is deleted.
            </Text>

            <FormControl>
              <FormLabel>Target family</FormLabel>
              <ChakraAsyncSelect
                value={target}
                onChange={(option) => setTarget((option as MasterProductFamilyOption) ?? null)}
                loadOptions={loadOptions}
                defaultOptions
                isClearable
                placeholder='Search and select a target family...'
                noOptionsMessage={({ inputValue }) => (inputValue ? 'No families found' : 'Type to search families')}
              />
            </FormControl>

            {target && isPreviewLoading && (
              <HStack justify='center' py='4'>
                <Spinner size='sm' color='red.500' />
              </HStack>
            )}

            {target && isPreviewError && <Text color='red.500'>Failed to load merge preview</Text>}

            {summary && (
              <Box borderWidth='1px' borderColor='gray.200' rounded='md' p='3'>
                <Text fontWeight='medium'>{summary.movedLine}</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' flexShrink={0} onClick={onClose} isDisabled={isMerging}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            minW='0'
            onClick={handleConfirm}
            isDisabled={!canConfirm}
            isLoading={isMerging}
            loadingText='Merging...'
            title={`Merge & delete '${sourceFamily.universalName}'`}>
            <Box as='span' isTruncated minW='0'>
              Merge & delete &lsquo;{sourceFamily.universalName}&rsquo;
            </Box>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
